import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";

// Extend AxiosRequestConfig with custom properties
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retried?: boolean;
}

// Get API base URLs
const getApiBaseUrls = (): string[] => {
  const envUrl = process.env.REACT_APP_API_URL;
  const urls: string[] = [];
  if (envUrl && /^https?:\/\//.test(envUrl)) {
    urls.push(envUrl);
  }

  // Add fallback URL from environment variable
  const fallbackUrl = process.env.REACT_APP_API_URL;
  if (fallbackUrl && /^https?:\/\//.test(fallbackUrl)) {
    urls.push(fallbackUrl);
  }
  if (urls.length === 0) {
    throw new Error("No valid API base URLs configured.");
  }
  return urls;
};

const API_BASE_URLS = getApiBaseUrls();

// Token management
const getToken = (): string | null => {
  return localStorage.getItem("token");
};

const clearTokens = (): void => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
};

// Create axios instance
const createApiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URLS[0],
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
createApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor
createApiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;

    // Handle 401 Unauthorized (Token expired or invalid)
    if (error.response?.status === 401) {
      // Clear tokens from localStorage
      clearTokens();
      
      // Only redirect if we're not already on the login page
      // This prevents infinite redirect loops
      const currentPath = window.location.pathname;
      if (currentPath !== "/login" && currentPath !== "/register" && currentPath !== "/forgot-password") {
        // Dispatch a custom event that can be listened to by the app
        // This allows Redux state to be updated before redirect
        window.dispatchEvent(new CustomEvent("auth:tokenExpired"));
        
        // Redirect to login page
        window.location.href = "/login";
      }
      
      return Promise.reject(error);
    }

    // Fallback to alternate URLs if available
    if (
      originalRequest &&
      !originalRequest._retried &&
      API_BASE_URLS.length > 1 &&
      typeof originalRequest.baseURL === "string"
    ) {
      originalRequest._retried = true;
      const currentUrlIndex = API_BASE_URLS.indexOf(originalRequest.baseURL);
      const nextUrlIndex = (currentUrlIndex + 1) % API_BASE_URLS.length;
      originalRequest.baseURL = API_BASE_URLS[nextUrlIndex];
      console.warn(`Retrying with alternate URL: ${originalRequest.baseURL}`);
      return createApiClient(originalRequest);
    }

    return Promise.reject(error);
  }
);

export default createApiClient;
